import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { requestAirshowReview } from "./airshowReviewApi";
import { requestAirshowSolidPreview } from "./airshowSolidApi";
import {
  AirshowComparisonViewer,
  type AirshowCameraPreset,
  type AirshowComparisonSide,
} from "./AirshowComparisonViewer";
import {
  AIRSHOW_IDENTITY_ALIGNMENT,
  type AirshowReviewLayout,
  type AirshowReviewResponse,
} from "./airshowReviewContract";
import type { AirshowRepresentationRow } from "./airshowRepresentationModel";
import type { AirshowSolidPreviewAvailable } from "./airshowSolidContract";
import "./airshow-review.css";

interface AirshowCoverageComparisonProps {
  row: AirshowRepresentationRow;
  projectId: string;
  branch: string;
}

type SolidMode = "components" | "solid";
type SolidBuildState =
  | { status: "idle" }
  | { status: "loading"; selectionKey: string }
  | { status: "available"; selectionKey: string; preview: AirshowSolidPreviewAvailable }
  | {
    status: "blocked" | "unavailable" | "rejected";
    selectionKey: string;
    code: string;
    message: string;
  };

function fidelityLabel(review: AirshowReviewResponse): string {
  if (review.loom.status === "unavailable") return "Unavailable";
  if (review.loom.fidelity === "exact-attested") return "Exact controls · attested";
  if (review.loom.fidelity === "exact-unattested") return "Bézier controls · proof incomplete";
  return review.loom.representation === "sampled-grid"
    ? "Adapted · sampled grid" : "Adapted · tessellated";
}

function comparisonSides(
  row: AirshowRepresentationRow,
  review: AirshowReviewResponse | null,
  loading: boolean,
  error: string | null,
): { reference: AirshowComparisonSide; loom: AirshowComparisonSide } {
  if (!review) {
    const status = loading ? "loading" : "unavailable";
    return {
      reference: {
        modelId: row.entryId, status, geometryMode: "unavailable", modelUrl: null,
        fidelityCode: "unavailable", fidelityLabel: loading ? "Loading reference…" : "Unavailable",
        unavailableReason: error ?? "Historical reference unavailable",
      },
      loom: {
        modelId: row.entryId, status, geometryMode: "unavailable", modelUrl: null,
        fidelityCode: "unavailable", fidelityLabel: loading ? "Building Loom display…" : "Unavailable",
        unavailableReason: error ?? "Loom display unavailable",
      },
    };
  }
  return {
    reference: review.reference.status === "available" ? {
      modelId: row.entryId, status: "available", geometryMode: "tessellated",
      modelUrl: review.reference.model_url, bbox: review.reference.bbox,
      fidelityCode: "historical-reference", fidelityLabel: "Archived OpenVSP reference",
    } : {
      modelId: row.entryId, status: "unavailable", geometryMode: "unavailable", modelUrl: null,
      fidelityCode: "unavailable", fidelityLabel: "Unavailable",
      unavailableReason: review.reference.message,
    },
    loom: review.loom.status === "available" ? {
      modelId: row.entryId, status: "available", geometryMode: review.loom.representation,
      modelUrl: review.loom.model_url, bbox: review.loom.bbox,
      fidelityCode: review.loom.fidelity, fidelityLabel: fidelityLabel(review),
    } : {
      modelId: row.entryId, status: "unavailable", geometryMode: "unavailable", modelUrl: null,
      fidelityCode: "unavailable", fidelityLabel: "Unavailable",
      unavailableReason: review.loom.message,
    },
  };
}

function missing(realized: number | null, source: number | null): number | null {
  return realized === null || source === null ? null : Math.max(source - realized, 0);
}

function plural(value: number, noun: string): string {
  return `${value} ${noun}${value === 1 ? "" : "s"} missing`;
}

function skippedSurfaceLabel(item: Readonly<Record<string, unknown>>, index: number): string {
  const text = (key: string) => typeof item[key] === "string" && item[key] ? item[key] as string : null;
  const number = (key: string) => Number.isInteger(item[key]) ? item[key] as number : null;
  const component = text("component_name") ?? text("geom_name") ?? text("geom_id") ?? `Component ${index + 1}`;
  const surface = number("surface_index") ?? number("surface_ordinal");
  const reason = text("code") ?? text("reason") ?? text("message") ?? "surface realization failed";
  return `${component}${surface === null ? "" : ` · surface ${surface}`} · ${reason}`;
}

function SolidProofCard({ preview }: { preview: AirshowSolidPreviewAvailable }) {
  const qc = preview.qc;
  const proof = preview.step_proof;
  return <aside aria-label="Watertight solid proof" className="airshow-solid-proof" data-testid="airshow-solid-proof">
    <header>
      <div><span>Verified CAD assembly</span><strong>Smooth trimmed STEP assembly</strong></div>
      <div><a download href={preview.step_url}>Download STEP</a><a download href={preview.stl_url}>Download STL</a></div>
    </header>
    <p className="airshow-solid-caveat">Exact OpenVSP source surfaces with tolerance-attested fitted trims — not exact algebraic intersections, an OpenVSP round-trip, or accepted Loom geometry</p>
    <dl className="airshow-solid-scope">
      <div><dt>Included</dt><dd>{preview.represented_components.length} / {preview.source_component_count} components</dd></div>
      <div><dt>Omitted</dt><dd>{preview.omitted_components.length} component{preview.omitted_components.length === 1 ? "" : "s"}</dd></div>
    </dl>
    <dl className="airshow-solid-qc">
      <div><dt>Boundary edges</dt><dd>{qc.boundary_edges}</dd></div>
      <div><dt>Non-manifold edges</dt><dd>{qc.nonmanifold_edges}</dd></div>
      <div><dt>Winding</dt><dd>{qc.winding_consistent ? "Consistent" : "Rejected"}</dd></div>
      <div><dt>Self-intersections</dt><dd>{qc.self_intersections}</dd></div>
      <div><dt>Watertight solids</dt><dd>{qc.connected_components}</dd></div>
      <div><dt>Signed volume</dt><dd>{qc.signed_volume.toPrecision(6)} · positive</dd></div>
      <div><dt>Trim pcurves</dt><dd>{proof.step_topology.pcurve_count}</dd></div>
      <div><dt>Independent kernel</dt><dd>{proof.independent_reimport.kernel}</dd></div>
    </dl>
    <details className="airshow-solid-omissions">
      <summary>{proof.solids.length} named solid{proof.solids.length === 1 ? "" : "s"}</summary>
      <ul>{proof.solids.map((solid) => <li key={solid.body_id}>
        <strong>{solid.name}</strong><code>{solid.role} · {solid.face_count} faces</code>
        <span>{solid.volume_mm3.toPrecision(7)} mm³ · 1 shell · 0 free edges</span>
      </li>)}</ul>
    </details>
    {preview.omitted_components.length ? <details className="airshow-solid-omissions">
      <summary>{preview.omitted_components.length} source component{preview.omitted_components.length === 1 ? "" : "s"} omitted</summary>
      <ul>{preview.omitted_components.map((item) => <li key={item.source_index}>
        <strong>{item.name}</strong><code>{item.type} · {item.code}</code><span>{item.message}</span>
      </li>)}</ul>
    </details> : null}
  </aside>;
}

export function AirshowCoverageComparison({ row, projectId, branch }: AirshowCoverageComparisonProps) {
  const [review, setReview] = useState<AirshowReviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [layout, setLayout] = useState<AirshowReviewLayout>("overlay");
  const [opacity, setOpacity] = useState(0.48);
  const [visibility, setVisibility] = useState({ reference: true, loom: true });
  const [solidMode, setSolidMode] = useState<SolidMode>("components");
  const [solidState, setSolidState] = useState<SolidBuildState>({ status: "idle" });
  const [cameraCommand, setCameraCommand] = useState<{ id: number; preset: AirshowCameraPreset }>({
    id: 0, preset: "isometric",
  });
  const requestSequence = useRef(0);
  const solidRequestSequence = useRef(0);
  const solidRequestController = useRef<AbortController | null>(null);
  const identity = `${row.entryId}\u001f${row.archiveMemberPath}\u001f${row.sourceSha256 ?? "missing"}`;

  useEffect(() => {
    const sequence = ++requestSequence.current;
    const controller = new AbortController();
    setReview(null);
    setError(null);
    if (!row.sourceSha256) {
      setLoading(false);
      setError("The source digest is unavailable, so no source-bound visual comparison can be built.");
      return () => controller.abort();
    }
    setLoading(true);
    void requestAirshowReview({
      projectId, branch, resolution: 12, signal: controller.signal,
      entry: {
        id: row.entryId,
        archiveMemberPath: row.archiveMemberPath,
        sourceSha256: row.sourceSha256,
      },
    }).then((nextReview) => {
      if (controller.signal.aborted || sequence !== requestSequence.current) return;
      setReview(nextReview);
      setLoading(false);
    }).catch((reason: unknown) => {
      if (controller.signal.aborted || sequence !== requestSequence.current) return;
      setError(reason instanceof Error ? reason.message : "The visual comparison could not be built.");
      setLoading(false);
    });
    return () => controller.abort();
  }, [branch, identity, projectId, row.archiveMemberPath, row.entryId, row.sourceSha256]);

  const reviewBundleKey = review?.loom.status === "available" ? review.loom.bundle_key : null;
  const solidSelectionKey = `${projectId}\u001f${branch}\u001f${identity}\u001f${reviewBundleKey ?? "no-review"}`;

  useEffect(() => {
    solidRequestSequence.current += 1;
    solidRequestController.current?.abort();
    solidRequestController.current = null;
    setSolidMode("components");
    setSolidState({ status: "idle" });
    return () => solidRequestController.current?.abort();
  }, [branch, identity, projectId, reviewBundleKey]);

  const activeSolidState: SolidBuildState = solidState.status !== "idle"
      && solidState.selectionKey === solidSelectionKey ? solidState : { status: "idle" };
  const effectiveSolidMode: SolidMode = solidMode === "solid"
      && activeSolidState.status === "available" ? "solid" : "components";

  const componentSides = useMemo(
    () => comparisonSides(row, review, loading, error),
    [error, loading, review, row],
  );
  const sides = useMemo(() => {
    if (effectiveSolidMode !== "solid" || activeSolidState.status !== "available") {
      return componentSides;
    }
    const preview = activeSolidState.preview;
    return {
      reference: componentSides.reference,
      loom: {
        modelId: `${row.entryId}:solid:${preview.solid_bundle_key}`,
        status: "available" as const,
        geometryMode: "tessellated" as const,
        modelUrl: preview.model_url,
        fidelityCode: preview.representation,
        fidelityLabel: "Derived watertight manifold mesh",
      },
    };
  }, [activeSolidState, componentSides, effectiveSolidMode, row.entryId]);
  const componentGap = missing(row.realizedComponentCount, row.sourceComponentCount);
  const surfaceGap = missing(row.realizedSurfaceCount, row.sourceSurfaceCount);
  const displayComplete = row.renderable && componentGap === 0 && surfaceGap === 0;
  const skippedSurfaces = review?.loom.skipped_surfaces.length ?? 0;
  const canBuildSolid = Boolean(row.sourceSha256 && reviewBundleKey);
  const ignoreCamera = useCallback(() => undefined, []);
  const ignoreAlignment = useCallback(() => undefined, []);
  const chooseCamera = (preset: AirshowCameraPreset) => {
    setCameraCommand((current) => ({ id: current.id + 1, preset }));
  };
  const buildSolid = () => {
    if (!row.sourceSha256 || !reviewBundleKey || activeSolidState.status === "loading"
        || activeSolidState.status === "available") return;
    solidRequestController.current?.abort();
    const controller = new AbortController();
    const sequence = ++solidRequestSequence.current;
    solidRequestController.current = controller;
    setSolidMode("components");
    setSolidState({ status: "loading", selectionKey: solidSelectionKey });
    void requestAirshowSolidPreview({
      projectId,
      entryId: row.entryId,
      archiveMemberPath: row.archiveMemberPath,
      sourceSha256: row.sourceSha256,
      reviewBundleKey,
      signal: controller.signal,
    }).then((response) => {
      if (controller.signal.aborted || sequence !== solidRequestSequence.current) return;
      solidRequestController.current = null;
      if (response.status === "available") {
        setSolidState({ status: "available", selectionKey: solidSelectionKey, preview: response });
        return;
      }
      setSolidMode("components");
      setSolidState({
        status: response.status,
        selectionKey: solidSelectionKey,
        code: response.code,
        message: response.message,
      });
    }).catch((reason: unknown) => {
      if (controller.signal.aborted || sequence !== solidRequestSequence.current
          || (reason instanceof Error && reason.name === "AbortError")) return;
      solidRequestController.current = null;
      setSolidMode("components");
      setSolidState({
        status: "rejected",
        selectionKey: solidSelectionKey,
        code: "solid-proof-rejected",
        message: reason instanceof Error ? reason.message : "The solid proof response was rejected.",
      });
    });
  };

  return <section className="airshow-review airshow-coverage-comparison" data-layout={layout} data-testid="airshow-coverage-comparison">
    <header className="airshow-coverage-comparison-header">
      <div><span>Selected aircraft · visual evidence</span><h3>{row.label}</h3><p>
        Amber is the archived OpenVSP reference. Cyan is Loom’s component assembly by default.
        In overlay mode, amber-only regions are the fastest way to spot missing or displaced geometry.
        A source-bound smooth STEP assembly is built only on request and independently re-imported before download.
      </p></div>
      <div className="airshow-coverage-legend" aria-label="Comparison legend">
        <span data-side="reference"><i />OpenVSP reference</span>
        <span data-side="loom"><i />{effectiveSolidMode === "solid" ? "Watertight solid" : "Loom display"}</span>
      </div>
    </header>

    <div className="airshow-coverage-comparison-controls">
      <div role="group" aria-label="Comparison layout">
        <button aria-pressed={layout === "overlay"} onClick={() => setLayout("overlay")} type="button">Overlay</button>
        <button aria-pressed={layout === "split"} onClick={() => setLayout("split")} type="button">Side by side</button>
      </div>
      <div role="group" aria-label="Loom geometry">
        <button aria-pressed={effectiveSolidMode === "components"} onClick={() => setSolidMode("components")} type="button">Components</button>
        <button
          aria-pressed={effectiveSolidMode === "solid"}
          disabled={activeSolidState.status !== "available"}
          onClick={() => setSolidMode("solid")}
          type="button"
        >Watertight solid</button>
      </div>
      <button
        className="airshow-solid-build"
        disabled={!canBuildSolid || activeSolidState.status === "loading" || activeSolidState.status === "available"}
        onClick={buildSolid}
        type="button"
      >{activeSolidState.status === "loading" ? "Building watertight solid…" : activeSolidState.status === "available" ? "Solid ready" : "Build watertight solid"}</button>
      <label><input checked={visibility.reference} onChange={(event) => setVisibility((current) => ({ ...current, reference: event.currentTarget.checked }))} type="checkbox" />OpenVSP</label>
      <label><input checked={visibility.loom} onChange={(event) => setVisibility((current) => ({ ...current, loom: event.currentTarget.checked }))} type="checkbox" />Loom</label>
      <label className="airshow-coverage-opacity"><span>Reference opacity</span><input aria-label="Reference opacity" disabled={layout !== "overlay"} max="0.9" min="0.08" onChange={(event) => setOpacity(Number(event.currentTarget.value))} step="0.01" type="range" value={opacity} /></label>
      <div role="group" aria-label="Camera view">
        {(["front", "side", "top", "isometric", "reset"] as const).map((preset) => <button key={preset} onClick={() => chooseCamera(preset)} type="button">{preset === "isometric" ? "Iso" : preset[0]!.toUpperCase() + preset.slice(1)}</button>)}
      </div>
      {activeSolidState.status === "loading" ? <p className="airshow-solid-status" data-state="loading" data-testid="airshow-solid-status" role="status">Building smooth trimmed STEP solids and independently verifying every shell…</p> : null}
      {activeSolidState.status === "blocked" || activeSolidState.status === "unavailable" || activeSolidState.status === "rejected" ? <p className="airshow-solid-status" data-state="error" data-testid="airshow-solid-status" role="alert"><code>{activeSolidState.code}</code><span>{activeSolidState.message}</span></p> : null}
      {activeSolidState.status === "available" ? <SolidProofCard preview={activeSolidState.preview} /> : null}
    </div>

    <AirshowComparisonViewer
      alignmentMode="source-frame"
      cameraCommand={cameraCommand}
      layout={layout}
      loom={sides.loom}
      manualAlignment={AIRSHOW_IDENTITY_ALIGNMENT}
      onCameraEvidence={ignoreCamera}
      onResolvedAlignment={ignoreAlignment}
      overlayOpacity={opacity}
      reference={sides.reference}
      visibility={visibility}
    />

    <div className="airshow-coverage-outcomes">
      <article data-state={row.renderable ? "pass" : "fail"} data-testid="airshow-display-works">
        <span>What works in Loom</span><strong>{row.renderable ? "Complete aircraft display" : "Display is not complete"}</strong>
        <p>{row.displayRepresentation ?? "No validated display representation"} · {row.displayFidelity ?? "unavailable"}</p>
        <dl><div><dt>Components</dt><dd>{row.realizedComponentCount ?? "—"} / {row.sourceComponentCount ?? "—"} components</dd></div><div><dt>Surfaces</dt><dd>{row.realizedSurfaceCount ?? "—"} / {row.sourceSurfaceCount ?? "—"} surfaces</dd></div></dl>
      </article>
      <article data-state={displayComplete ? "pass" : "fail"} data-testid="airshow-display-gap">
        <span>What does not work in the display</span>
        <strong>{displayComplete ? "No missing display geometry" : "Loom display needs repair"}</strong>
        {componentGap !== null && componentGap > 0 ? <p>{plural(componentGap, "component")}</p> : null}
        {surfaceGap !== null && surfaceGap > 0 ? <p>{plural(surfaceGap, "surface")}</p> : null}
        {skippedSurfaces > 0 ? <p>{skippedSurfaces} skipped surface{skippedSurfaces === 1 ? "" : "s"} in the live visual artifact</p> : null}
        {review?.loom.skipped_surfaces.length ? <ul data-testid="airshow-skipped-surfaces">
          {review.loom.skipped_surfaces.slice(0, 12).map((item, index) => <li key={`${index}:${skippedSurfaceLabel(item, index)}`}>{skippedSurfaceLabel(item, index)}</li>)}
        </ul> : null}
        {!displayComplete ? <><code>{row.displayCode}</code><p>{row.displayMessage}</p></> : <p>Component and surface inventory matches the audited source.</p>}
      </article>
      <article data-state={row.exactAttested ? "pass" : "warn"} data-testid="airshow-exact-gap">
        <span>Independent exactness gate</span>
        <strong>{row.exactAttested ? "Exact Bézier operations enabled" : displayComplete ? "Display works; exact operations remain gated" : "Exact operations remain gated"}</strong>
        <code>{row.exactCode}</code><p>{row.exactMessage}</p>
      </article>
    </div>
  </section>;
}
